import { useState, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAppContext } from "@/components/DolphinLayout";
import {
  Export,
  DownloadSimple,
  UploadSimple,
  File,
  CheckCircle,
  ArrowsClockwise,
} from "@phosphor-icons/react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImportExport = () => {
  const { refreshStats, refreshFolders } = useAppContext();
  const [exporting, setExporting] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleExportAll = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/profiles-export`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], {
        type: "application/json",
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `profiles_${new Date().toISOString().split("T")[0]}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Профили экспортированы");
    } catch (e) {
      toast.error("Ошибка экспорта");
    } finally {
      setExporting(false);
    }
  };

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);
    setImportResult(null);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await axios.post(`${API}/profiles/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setImportResult({ success: true, message: res.data.message });
      toast.success(res.data.message);
      refreshStats?.();
      refreshFolders?.();
    } catch (e) {
      setImportResult({
        success: false,
        message: e.response?.data?.detail || "Ошибка импорта",
      });
      toast.error(e.response?.data?.detail || "Ошибка импорта");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  return (
    <div className="flex flex-col h-full" data-testid="import-export-page">
      {/* Top bar */}
      <div className="top-bar">
        <div className="top-bar-left">
          <Export size={24} weight="duotone" className="text-[#2196F3]" />
          <h1 className="text-lg font-semibold">Импорт / Экспорт</h1>
        </div>
      </div>

      {/* Content */}
      <div className="content-area">
        <div className="max-w-2xl mx-auto space-y-6">
          {/* Export */}
          <div className="bg-[#1A1D24] rounded-lg border border-[#252A33] p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#4CAF50]/20 rounded-lg flex items-center justify-center">
                <DownloadSimple size={24} className="text-[#4CAF50]" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold mb-1">Экспорт профилей</h2>
                <p className="text-sm text-[#8A9099] mb-4">
                  Скачайте все профили в формате JSON для переноса на другое устройство
                </p>
                <button
                  onClick={handleExportAll}
                  disabled={exporting}
                  className="action-btn action-btn-success"
                  data-testid="export-all-btn"
                >
                  {exporting ? (
                    <>
                      <ArrowsClockwise size={14} className="animate-spin" />
                      Экспорт...
                    </>
                  ) : (
                    <>
                      <DownloadSimple size={14} />
                      Экспортировать все
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Import */}
          <div className="bg-[#1A1D24] rounded-lg border border-[#252A33] p-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 bg-[#2196F3]/20 rounded-lg flex items-center justify-center">
                <UploadSimple size={24} className="text-[#2196F3]" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-semibold mb-1">Импорт профилей</h2>
                <p className="text-sm text-[#8A9099] mb-4">
                  Загрузите файл JSON с профилями
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".json"
                  onChange={handleFileSelect}
                  className="hidden"
                  data-testid="import-file-input"
                />

                <div
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-[#252A33] rounded-lg p-8 text-center cursor-pointer hover:border-[#2196F3] hover:bg-[#2196F3]/5 transition-colors"
                  data-testid="import-dropzone"
                >
                  {importing ? (
                    <div className="flex flex-col items-center">
                      <ArrowsClockwise size={32} className="animate-spin text-[#2196F3] mb-2" />
                      <p className="text-sm text-[#8A9099]">Импорт...</p>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center">
                      <File size={32} className="text-[#5C6370] mb-2" />
                      <p className="text-sm font-medium">Нажмите для выбора файла</p>
                      <p className="text-xs text-[#5C6370] mt-1">или перетащите сюда</p>
                    </div>
                  )}
                </div>

                {importResult && (
                  <div
                    className={`flex items-center gap-3 p-4 rounded mt-4 ${
                      importResult.success
                        ? "bg-[#4CAF50]/10 border border-[#4CAF50]/30"
                        : "bg-[#F44336]/10 border border-[#F44336]/30"
                    }`}
                  >
                    <CheckCircle
                      size={20}
                      className={importResult.success ? "text-[#4CAF50]" : "text-[#F44336]"}
                    />
                    <p
                      className={`text-sm ${
                        importResult.success ? "text-[#4CAF50]" : "text-[#F44336]"
                      }`}
                    >
                      {importResult.message}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Format info */}
          <div className="bg-[#1A1D24] rounded-lg border border-[#252A33] p-6">
            <h2 className="text-sm font-semibold text-[#8A9099] mb-4">ФОРМАТ ФАЙЛА</h2>
            <div className="space-y-4 text-sm">
              <div>
                <p className="text-[#8A9099] mb-2">Один профиль:</p>
                <pre className="bg-[#0D0F12] p-3 rounded text-xs font-mono text-[#8A9099]">
{`{ "name": "Профиль", "fingerprint": {...} }`}
                </pre>
              </div>
              <div>
                <p className="text-[#8A9099] mb-2">Несколько профилей:</p>
                <pre className="bg-[#0D0F12] p-3 rounded text-xs font-mono text-[#8A9099]">
{`{ "profiles": [{ "name": "1" }, { "name": "2" }] }`}
                </pre>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ImportExport;
